import React, { createContext, useContext, useState, useMemo } from 'react';
import { validatePayment, normalizePayment } from '../utils/payments-utils';

const PaymentContext = createContext();

export const PaymentProvider = ({ children, initialPayments = [], onUpdatePayments }) => {
  const [payments, setPayments] = useState(initialPayments);

  const addPayment = (newPayment) => {
    const normalizedPayment = normalizePayment(newPayment);
    const { isValid, errors } = validatePayment(normalizedPayment);
    
    if (isValid) {
      const updatedPayments = [...payments, normalizedPayment];
      setPayments(updatedPayments);
      onUpdatePayments?.(updatedPayments);
      return { success: true, payment: normalizedPayment };
    }
    
    return { success: false, errors };
  };

  const updatePayment = (updatedPayment) => {
    const normalizedPayment = normalizePayment(updatedPayment);
    const { isValid, errors } = validatePayment(normalizedPayment);
    
    if (isValid) {
      const updatedPayments = payments.map(payment => 
        payment.id === normalizedPayment.id ? normalizedPayment : payment
      );
      setPayments(updatedPayments);
      onUpdatePayments?.(updatedPayments);
      return { success: true, payment: normalizedPayment };
    }
    
    return { success: false, errors };
  };

  const deletePayment = (paymentId) => {
    const updatedPayments = payments.filter(payment => payment.id !== paymentId);
    setPayments(updatedPayments);
    onUpdatePayments?.(updatedPayments);
    return updatedPayments;
  };

  const calculateTotalPayments = useMemo(() => {
    return payments.reduce((total, payment) => {
      const paymentTotal = payment.unitAmounts?.reduce(
        (sum, item) => sum + (parseFloat(item.amount) || 0),
        0
      ) || 0;
      return total + paymentTotal;
    }, 0);
  }, [payments]);

  const contextValue = {
    payments,
    addPayment,
    updatePayment,
    deletePayment,
    calculateTotalPayments,
    setPayments
  };

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayments = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayments must be used within a PaymentProvider');
  }
  return context;
};